import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { PartnerStore } from '@app/config/partner-store';
import { Store } from '@app/config/store';
import { password, passwordMatchValidator } from '@app/dto/common.dto';
import { SetPassword, SetPasswordService } from '@app/services/set-password.service';
import { TinsService } from '@app/services/tins.service';
import { EncryptionService } from '@app/services/encrytion.service';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { matRemoveRedEye, matVisibilityOff } from '@ng-icons/material-icons/baseline';

@Component({
  selector: 'app-set-password',
  standalone: true,
  imports: [RouterModule, ReactiveFormsModule, CommonModule, NgIconComponent],
  providers: [
    provideIcons({
      matRemoveRedEye,
      matVisibilityOff,
    }),
  ],
  templateUrl: './set-password.component.html',
  styleUrl: './set-password.component.css',
})
export class SetPasswordComponent {
  passwordFieldType: string = 'password';
  loading: boolean = false;

  passwordForm: FormGroup = new FormGroup(
    {
      email: new FormControl({ value: '', disabled: true }),
      password: new FormControl('', [Validators.required, password]),
      confirmPassword: new FormControl('', [Validators.required, password]),
      token: new FormControl('', []),
    },
    { validators: passwordMatchValidator() },
  );
  redirectURL: string = '';

  constructor(
    protected store: Store,
    private router: Router,
    private setPasswordSvc: SetPasswordService,
    private tinService: TinsService,
    private partnerStore: PartnerStore,
    private encrypt: EncryptionService,
  ) {
    const token = this.router.parseUrl(this.router.url).queryParams['token'];
    if (!token || token === '' || token?.split('.').length !== 3) {
      this.router.navigate(['/login']);
      return;
    }

    const payload = JSON.parse(atob(token.split('.')[1]));
    this.passwordForm.controls['email'].setValue(payload.ue);
    this.passwordForm.controls['token'].setValue(token);

    const redirectUrl = this.router.parseUrl(this.router.url).queryParams['redirectUrl'];

    if (redirectUrl && redirectUrl !== '') sessionStorage.setItem('redirectUrl', redirectUrl);

    this.redirectURL = sessionStorage.getItem('redirectUrl') ?? '';
    sessionStorage.removeItem('redirectUrl');
  }

  async setPassword(): Promise<void> {
    if (this.loading) return;

    if (!this.passwordForm.valid) {
      this.passwordForm.markAllAsTouched();
      return;
    }

    this.loading = true;

    try {
      const raw = this.passwordForm.getRawValue() as {
        email: string;
        password: string;
        confirmPassword: string;
        token: string;
      };

      const pwd = raw.password;
      const confirmPwd = raw.confirmPassword;

      const [encPassword] = await Promise.all([
        this.encrypt.encryptWithPublicKey(pwd),
        this.encrypt.encryptWithPublicKey(confirmPwd),
      ]);

      const payload: SetPassword = {
        email: raw.email,
        password: encPassword,
        token: raw.token,
      };

      const isPartnerRoute = this.router.url.includes('/partner');

      if (!isPartnerRoute) {
        this.setPasswordSvc.setPassword(payload).subscribe((resp) => {
          if (resp) {
            this.store.setTaxpayer(resp);
            this.router.navigate(['/redirect']);
          }
        });
      } else {
        this.setPasswordSvc.setPasswordPartner(payload).subscribe((resp) => {
          if (resp) {
            this.partnerStore.setAuthToken(resp.token);
            this.partnerStore.setUserInfo({
              email: resp.userEmail,
              name: resp.userName,
            });

            const query = this.redirectURL !== '' ? { redirectUrl: this.redirectURL } : {};

            this.router.navigate(['/admin-redirect'], { queryParams: query });
          }
        });
      }
    } catch (err) {
      console.error('Encryption / setPassword error:', err);
    } finally {
      this.loading = false;
    }
  }

  togglePasswordVisibility() {
    this.passwordFieldType = this.passwordFieldType === 'password' ? 'text' : 'password';
  }
}
